/**
 * @plasma/logger — Shared structured logging for all Plasma apps.
 *
 * Platform-agnostic core. Each app calls `initLogger()` once at startup
 * with platform-specific config (env vars, device info, etc.).
 */

// ── Types ────────────────────────────────────────────────────────────────────

type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerConfig {
  service: string;
  environment: string;
  isDev: boolean;
  platform: string;
  appVersion?: string;
  axiomToken?: string;
  axiomDataset?: string;
  axiomOrgId?: string;
  /** Device metadata for native apps */
  deviceInfo?: {
    brand?: string;
    model?: string;
    osName?: string;
    osVersion?: string;
  };
  /** Fetch timeout in ms. Native apps should use 10_000. Web can omit. */
  fetchTimeout?: number;
}

// Base context always present
interface BaseContext {
  service: string;
  environment: string;
  platform: string;
  app_version?: string;
  [key: string]: unknown;
}

// LogContext is just BaseContext with arbitrary extra fields
type LogContext = BaseContext & Record<string, unknown>;

interface LogEvent extends LogContext {
  _time: string;
  level: LogLevel;
  event: string;
  message?: string;
}

// ── Sensitive data redaction ─────────────────────────────────────────────────

const SENSITIVE_PATTERNS = [
  /token/i,
  /secret/i,
  /password/i,
  /api[_-]?key/i,
  /auth/i,
  /bearer/i,
  /credential/i,
  /private/i,
  /card/i,
  /cvv/i,
  /ssn/i,
];

/**
 * Sanitize data to remove potentially sensitive information
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const isSensitive = SENSITIVE_PATTERNS.some((pattern) => pattern.test(key));

    if (isSensitive) {
      sanitized[key] = "[REDACTED]";
    } else if (isPlainObject(value)) {
      sanitized[key] = sanitizeData(value);
    } else if (value instanceof Error) {
      sanitized[key] = { message: value.message, name: value.name };
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

// ── Module-level state ───────────────────────────────────────────────────────

let config: LoggerConfig | null = null;
const eventBuffer: LogEvent[] = [];
let flushPending = false;

// ── Axiom ingest ─────────────────────────────────────────────────────────────

async function sendToAxiom(events: LogEvent[]): Promise<boolean> {
  if (!config?.axiomToken || events.length === 0) {
    return false;
  }

  const dataset = config.axiomDataset ?? "plasma-logs";
  const fetchOptions: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${config.axiomToken}`,
      "Content-Type": "application/json",
      ...(config.axiomOrgId && { "X-Axiom-Org-Id": config.axiomOrgId }),
    },
    body: JSON.stringify(events),
  };

  // If fetchTimeout is set (native apps), use AbortController
  if (config.fetchTimeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.fetchTimeout);

    try {
      const response = await fetch(
        `https://api.axiom.co/v1/datasets/${dataset}/ingest`,
        {
          ...fetchOptions,
          signal: controller.signal as RequestInit["signal"],
        }
      );
      return response.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  try {
    const response = await fetch(
      `https://api.axiom.co/v1/datasets/${dataset}/ingest`,
      fetchOptions
    );
    return response.ok;
  } catch {
    return false;
  }
}

function scheduleFlush(): void {
  if (flushPending) {
    return;
  }

  flushPending = true;
  setTimeout(() => {
    flushPending = false;
    const events = [...eventBuffer];
    eventBuffer.length = 0;
    sendToAxiom(events).catch(() => {
      // Silently ignore - logging should never break the app
    });
  }, 1000);
}

/**
 * Log to console in development with appropriate log level
 */
function logToConsole(
  level: LogLevel,
  prefix: string,
  data: Record<string, unknown> | ""
): void {
  switch (level) {
    case "debug":
      console.debug(prefix, data);
      break;
    case "info":
      console.info(prefix, data);
      break;
    case "warn":
      console.warn(prefix, data);
      break;
    case "error":
      console.error(prefix, data);
      break;
    default:
      console.info(prefix, data);
  }
}

// ── Logger class ─────────────────────────────────────────────────────────────

class Logger {
  private readonly context: LogContext;

  constructor(context?: Partial<LogContext>) {
    if (context && Object.keys(context).length > 0) {
      this.context = {
        service: config?.service ?? "unknown",
        environment: config?.environment ?? "unknown",
        platform: config?.platform ?? "unknown",
        ...(config?.appVersion && { app_version: config.appVersion }),
        ...(config?.deviceInfo?.brand && {
          device_brand: config.deviceInfo.brand,
        }),
        ...(config?.deviceInfo?.model && {
          device_model: config.deviceInfo.model,
        }),
        ...(config?.deviceInfo?.osName && {
          os_name: config.deviceInfo.osName,
        }),
        ...(config?.deviceInfo?.osVersion && {
          os_version: config.deviceInfo.osVersion,
        }),
        ...context,
      };
    } else {
      this.context = {
        service: config?.service ?? "unknown",
        environment: config?.environment ?? "unknown",
        platform: config?.platform ?? "unknown",
        ...(config?.appVersion && { app_version: config.appVersion }),
        ...(config?.deviceInfo?.brand && {
          device_brand: config.deviceInfo.brand,
        }),
        ...(config?.deviceInfo?.model && {
          device_model: config.deviceInfo.model,
        }),
        ...(config?.deviceInfo?.osName && {
          os_name: config.deviceInfo.osName,
        }),
        ...(config?.deviceInfo?.osVersion && {
          os_version: config.deviceInfo.osVersion,
        }),
      };
    }
  }

  /**
   * Core logging method
   */
  private log(
    level: LogLevel,
    event: string,
    data: Record<string, unknown> = {}
  ): void {
    const sanitizedData = sanitizeData(data);

    const logEvent: LogEvent = {
      _time: new Date().toISOString(),
      level,
      event,
      ...this.context,
      ...sanitizedData,
    };

    const isDev = config?.isDev ?? true;

    if (isDev) {
      const prefix = `[${level.toUpperCase()}] ${event}`;
      const hasData = Object.keys(sanitizedData).length > 0;
      logToConsole(level, prefix, hasData ? sanitizedData : "");
    } else {
      eventBuffer.push(logEvent);

      if (level === "error") {
        const events = [...eventBuffer];
        eventBuffer.length = 0;
        sendToAxiom(events).catch(() => {
          // Silently ignore errors
        });
      } else {
        scheduleFlush();
      }
    }
  }

  // ── Log level methods ────────────────────────────────────────────────────

  debug(event: string, data?: Record<string, unknown>): void {
    this.log("debug", event, data);
  }

  info(event: string, data?: Record<string, unknown>): void {
    this.log("info", event, data);
  }

  warn(event: string, data?: Record<string, unknown>): void {
    this.log("warn", event, data);
  }

  error(event: string, data?: Record<string, unknown>): void {
    this.log("error", event, data);
  }

  /**
   * Log an exception with error details
   */
  exception(
    event: string,
    error: unknown,
    data?: Record<string, unknown>
  ): void {
    const err = error instanceof Error ? error : new Error(String(error));
    const isDev = config?.isDev ?? true;

    this.log("error", event, {
      error_type: err.name,
      error_message: err.message,
      ...(isDev && { error_stack: err.stack }),
      ...data,
    });
  }

  /**
   * Manually flush buffered events
   */
  async flush(): Promise<void> {
    if (eventBuffer.length === 0) {
      return;
    }

    const events = [...eventBuffer];
    eventBuffer.length = 0;
    await sendToAxiom(events);
  }

  // ── Context methods ──────────────────────────────────────────────────────

  /**
   * Add arbitrary context
   */
  with(extra: Record<string, unknown>): Logger {
    return new Logger({ ...this.context, ...extra });
  }

  withUser(user: {
    user_id?: string;
    user_email?: string;
    user_role?: string;
  }): Logger {
    return this.with(user);
  }

  withOrg(org: {
    organization_id?: string;
    organization_name?: string;
  }): Logger {
    return this.with(org);
  }

  withLocation(location: {
    location_id?: string;
    location_name?: string;
    location_slug?: string;
  }): Logger {
    return this.with(location);
  }

  withDevice(device: {
    device_id?: string;
    device_name?: string;
    device_type?: string;
    device_brand?: string;
    device_model?: string;
    os_name?: string;
    os_version?: string;
  }): Logger {
    return this.with(device);
  }

  withSession(session: { session_id?: string; employee_id?: string }): Logger {
    return this.with(session);
  }

  withCustomer(customer: {
    customer_email?: string;
    platform_customer_id?: string;
  }): Logger {
    return this.with(customer);
  }

  withStation(station: { station_id?: string; station_name?: string }): Logger {
    return this.with(station);
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialize the logger with platform-specific config.
 * Must be called once at app startup before logging.
 */
function initLogger(cfg: LoggerConfig): void {
  config = cfg;
}

/** Default logger instance. Call initLogger() before using. */
const logger = new Logger();

function createLogger(context?: Partial<LogContext>): Logger {
  return new Logger(context);
}

export { Logger, initLogger, createLogger, sanitizeData, logger };
export type { LogLevel, LogContext, LogEvent, LoggerConfig };
